(ns app.front.view.search.complaint.subs
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.front.dashboard.map :as dashboard-map]
            [app.front.dashboard.search :as dashboard-search]
            [app.front.entities :as entities]
            [app.front.view.complaint.add-form :as complaint-form]
            [app.front.view.complaint.new-case-view :as new-case-view]
            [app.front.view.complaint.search :as complaint-search]
            [app.front.view.complaint.subs :as complaint-subs]
            [app.front.view.complaint.util :as complaint-util]
            [app.front.view.workspace.get :as ws-get]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::data :-> ::data)

(defn search-active? [db]
  (= :view/search (get-in db [:route/match :data :name])))

(defn search-action-group [groups authority-group-ref]
  (let [authority-group-id (if (map? authority-group-ref)
                             (:db/id authority-group-ref)
                             authority-group-ref)
        group-id           (when (map? authority-group-ref)
                             (:group/id authority-group-ref))]
    (some (fn [{:keys [authority-groups] :as group}]
            (when-let [authority-group (p/find-first authority-groups
                                                     #(or (and authority-group-id
                                                               (= authority-group-id
                                                                  (:authority-group-id %)))
                                                          (and group-id
                                                               (= group-id (:group/id %)))))]
              (assoc group
                     :authority-groups [authority-group]
                     :complaint-sources (:complaint-sources authority-group))))
          groups)))

(defn- sort-groups-by-municipality-name [lang groups]
  (sort-by #(municipality-model/municipality-name % lang) groups))

(defn- set-groups [db status groups]
  (-> db
      (assoc-in [::data ::groups] (sort-groups-by-municipality-name (i18n/language-query db) groups))
      (assoc-in [::data ::groups-status] status)))

(defn- remember-selected-group [db]
  (cond-> db
    (not (contains? (get db ::data) ::selected-group-before-search))
    (assoc-in [::data ::selected-group-before-search]
              (get-in db [::complaint-subs/data ::complaint-subs/selected-group]))))

(defn- action-context-fx [db status groups authority-group-ref]
  (if-let [{[authority-group] :authority-groups :as selected-group}
           (search-action-group groups authority-group-ref)]
    (let [db (remember-selected-group db)]
      {:db (-> db
               (complaint-subs/set-selected-group selected-group)
               (assoc-in [::data ::action-group-id] (:group/id authority-group))
               (assoc-in [::data ::action-context-available?] true)
               (assoc-in [::data ::action-context-loading?] false))
       :fx [[:dispatch [::entities/merge-entity-db
                        (or status 200)
                        (->> (concat (:categories authority-group)
                                     (:complaint-sources authority-group))
                             du/entities->entity-db)]]
            [:dispatch [::ws-get/get-workspace-config (:group/id authority-group)]]]})
    {:db (assoc-in db [::data ::action-context-loading?] false)}))

(defn- complaint-command [id on-success]
  {:command    :complaint/get
   :data       {:complaint-id id}
   :method     :get
   :on-success on-success
   :on-error   [::load-complaint.done id]})

(defn begin-load [db id]
  (-> db
      (assoc-in [::data ::complaint-id] id)
      (assoc-in [::data ::action-context-loading?] true)
      (p/dissoc-in [::data ::complaint-loaded?])
      (p/dissoc-in [::data ::action-context-available?])
      (p/dissoc-in [::data ::action-group-id])))

(rf/reg-event-fx ::load-map-complaint
  (fn [{:keys [db]} [_ id]]
    (when (search-active? db)
      {:db           (begin-load db id)
       :comm/command (complaint-command id [::load-complaint.success id])})))

(rf/reg-event-fx ::show-complaint-modal
  (fn [{:keys [db]} [_ id]]
    (when (search-active? db)
      {:db           (-> db
                         (assoc ::complaint-subs/selected-complaint id)
                         (assoc-in [::complaint-subs/data ::complaint-subs/modal-loading?] true)
                         (begin-load id))
       :comm/command (assoc (complaint-command id [::load-complaint.success id])
                            :finally-dispatch [::complaint-subs/load-modal.done])})))

(defn load-complaint-success-fx [db complaint-id status response]
  (let [complaint           (p/find-first (vals response) {:complaint/id complaint-id})
        authority-group-ref (:domain/authority-group complaint)
        groups              (get-in db [::data ::groups])
        current?            (and (search-active? db)
                                 (= complaint-id (get-in db [::data ::complaint-id])))
        db                  (if current?
                              (assoc-in db [::data ::complaint-loaded?] true)
                              db)]
    (cond
      (not current?)
      {:db       db
       :dispatch [::entities/merge-entity-db status response]}

      (not (complaint-util/can-edit-complaint? complaint))
      {:db       (-> db
                     (p/dissoc-in [::data ::action-context-available?])
                     (assoc-in [::data ::action-context-loading?] false))
       :dispatch [::entities/merge-entity-db status response]}

      (seq groups)
      (let [{:keys [fx] :as context-fx}
            (action-context-fx db (get-in db [::data ::groups-status]) groups authority-group-ref)]
        (assoc context-fx :fx (into [[:dispatch [::entities/merge-entity-db status response]]] fx)))

      :else
      {:db           db
       :fx           [[:dispatch [::entities/merge-entity-db status response]]]
       :comm/command {:command    :complaint/groups-for-complaint-management
                      :method     :get
                      :on-success [::load-groups.success complaint-id authority-group-ref]
                      :on-error   [::load-groups.done complaint-id]}})))

(rf/reg-event-fx ::load-complaint.success
  (fn [{:keys [db]} [_ complaint-id status response]]
    (load-complaint-success-fx db complaint-id status response)))

(rf/reg-event-fx ::load-groups.success
  (fn [{:keys [db]} [_ complaint-id authority-group-ref status groups]]
    (let [db (set-groups db status groups)]
      (if (and (search-active? db)
               (= complaint-id (get-in db [::data ::complaint-id])))
        (action-context-fx db status (get-in db [::data ::groups]) authority-group-ref)
        {:db db}))))

(rf/reg-event-db ::load-groups.done
  (fn [db [_ complaint-id]]
    (cond-> db
      (= complaint-id (get-in db [::data ::complaint-id]))
      (assoc-in [::data ::action-context-loading?] false))))

(rf/reg-event-db ::load-complaint.done
  (fn [db [_ complaint-id]]
    (cond-> db
      (= complaint-id (get-in db [::data ::complaint-id]))
      (assoc-in [::data ::action-context-loading?] false))))

(defn clear-action-context [db complaint-id]
  (if (and complaint-id
           (not= complaint-id (get-in db [::data ::complaint-id])))
    db
    (let [data (get db ::data)
          db   (cond-> db
                 (contains? data ::selected-group-before-search)
                 (complaint-subs/set-selected-group (::selected-group-before-search data)))]
      (update db ::data dissoc
              ::selected-group-before-search
              ::action-context-available?
              ::action-context-loading?
              ::complaint-loaded?
              ::complaint-id
              ::action-group-id))))

(rf/reg-event-db ::clear-action-context
  (fn [db [_ complaint-id]]
    (clear-action-context db complaint-id)))

(defn- clear-point-state [db]
  (-> (p/dissoc-in db [::data ::point])
      (update ::complaint-subs/data dissoc
              ::complaint-subs/complaints-to-case
              ::complaint-subs/adding-complaints-to-case?
              ::complaint-subs/complaint-in-view)
      (dissoc ::complaint-form/data)))

(defn- drop-point [db]
  (-> (clear-action-context db nil)
      clear-point-state
      dashboard-map/deselect-point))

(rf/reg-event-db ::close-map-point
  (fn [db _]
    (drop-point db)))

(defn map-point-dismissed
  "The action context of a complaint result opened meanwhile is kept."
  [db point-id]
  (cond-> db
    (= point-id (get-in db [::data ::point :id]))
    (-> (cond-> (not (get-in db [::data ::complaint-id])) (clear-action-context nil))
        clear-point-state)))

(rf/reg-event-db ::map-point-dismissed
  (fn [db [_ point-id]]
    (map-point-dismissed db point-id)))

(rf/reg-event-fx ::cleanup
  (fn [_ _]
    {:fx [[:dispatch [::complaint-subs/hide-complaint-modal]]
          [:dispatch [::new-case-view/clear-state]]
          [:dispatch [::close-map-point]]]}))

(defn refresh-fx [db]
  (when (and (search-active? db)
             (= :complaint (dashboard-search/search-mode-name db)))
    ;; A map refresh with unchanged filters keeps the selected map point.
    {:dispatch (if (= :dashboard-search/map-view (dashboard-search/current-view db))
                 [::dashboard-map/map-search (dashboard-search/format-search-request db) nil]
                 dashboard-search/new-search-event)}))

(rf/reg-event-fx ::refresh
  (fn [{:keys [db]} _]
    (refresh-fx db)))

(defn cleanup-map-result-fx [db complaint-id {:keys [case-workspace-id]}]
  (when (and (search-active? db)
             (= complaint-id (get-in db [::data ::complaint-id])))
    (if case-workspace-id
      {:db (clear-action-context db complaint-id)
       :fx [[:dispatch [::dashboard-map/close-right-side-view]]
            [:dispatch dashboard-search/new-search-event]]}
      {:db       (clear-action-context db complaint-id)
       :dispatch [::dashboard-map/result-fetch complaint-id]})))


(rf/reg-event-fx ::cleanup-map-result
  (fn [{:keys [db]} [_ complaint-id cleanup-context]]
    (cleanup-map-result-fx db complaint-id cleanup-context)))


(defn cleanup-modal-result-fx [db complaint-id {:keys [case-workspace-id]}]
  (when (and (search-active? db)
             (= complaint-id (get-in db [::data ::complaint-id])))
    (p/assoc-if {:db (clear-action-context db complaint-id)}
                :dispatch (when case-workspace-id dashboard-search/new-search-event))))


(rf/reg-event-fx ::cleanup-modal-result
  (fn [{:keys [db]} [_ complaint-id cleanup-context]]
    (cleanup-modal-result-fx db complaint-id cleanup-context)))


(defn map-action-events [complaint-id]
  {:refresh-event [::refresh]
   :cleanup-event [::cleanup-map-result complaint-id]})

(defn modal-action-events [complaint-id]
  (assoc (map-action-events complaint-id)
         :cleanup-event [::cleanup-modal-result complaint-id]))


;;
;; Property selection on the map
;;

(defn scope-groups [db]
  (let [scope-group-ids (set (dashboard-search/get-data db ::dashboard-search/implicit-filter-values :group/id))]
    (cond->> (vec (get-in db [::data ::groups]))
      (seq scope-group-ids) (filterv #(contains? scope-group-ids (:group/id %))))))

(defn point-group [db groups]
  (let [dashboard-group (get-in db
                                [::data ::selected-group-before-search]
                                (get-in db [::complaint-subs/data ::complaint-subs/selected-group]))]
    (or (some #{(get-in db [::data ::point-group])} groups)
        (some #{dashboard-group} groups)
        (first groups))))

(defn current-point? [db point-id]
  (and point-id
       (search-active? db)
       (= :complaint (dashboard-search/search-mode-name db))
       (= point-id (get-in db [::data ::point :id]))
       (= point-id (:id (dashboard-map/selected-point db)))))

(defn- point-loading? [db point-id]
  (and (current-point? db point-id)
       (= :loading (get-in db [::data ::point :status]))))

(defn- property-complaints-command [db point-id]
  (let [{{:keys [authority-groups]} :group
         {:keys [geometry]}         :property} (get-in db [::data ::point])]
    (when geometry
      (:comm/command
        (complaint-search/find-complaints-cmd
          (map :authority-group-id authority-groups)
          geometry
          (->> (dashboard-search/format-search-request db)
               :conditions
               (filter #(= :domain/state (:filter.condition/attribute %))))
          [::property-complaints.success point-id])))))

(defn point-search-fx [db point-id]
  (let [groups                        (scope-groups db)
        {domain-group-id    :group/id
         :municipality/keys [number]
         :as                group}    (point-group db groups)]
    (if group
      {:db (-> (assoc-in db [::data ::point-group] group)
               (update-in [::data ::point] assoc :group group :groups groups))
       :fx [[:comm/command {:command          :address/point-search
                            :data             (p/assoc-if {:domain-group-id domain-group-id
                                                           :coordinates     (get-in db [::data ::point :coordinates])
                                                           :property-types  [:property.type/property]
                                                           :sourceCRS       "3857"}
                                                          :municipality number)
                            :on-success       [::point-search.success point-id]
                            :on-not-found     [::point-search.not-found point-id]
                            :on-error         [::point-failed point-id]
                            :finally-dispatch [::point-search.done point-id]}]]}
      {:db (drop-point db)})))

(defn select-map-point-fx [db point-id coordinates]
  (when (and (search-active? db)
             (= :complaint (dashboard-search/search-mode-name db)))
    (let [groups-loaded? (contains? (get db ::data) ::groups)]
      (when-not (and groups-loaded? (empty? (scope-groups db)))
        (let [point {:id point-id :coordinates coordinates}
              db    (-> (drop-point db)
                        (dashboard-map/select-point point)
                        (assoc-in [::data ::point] (assoc point :status :loading)))]
          (if groups-loaded?
            (point-search-fx db point-id)
            {:db           db
             :comm/command {:command          :complaint/groups-for-complaint-management
                            :method           :get
                            :on-success       [::point-groups.success point-id]
                            :on-error         [::point-failed point-id]
                            :finally-dispatch [::point-groups.done point-id]}}))))))

(rf/reg-event-fx ::select-map-point
  (fn [{:keys [db]} [_ coordinates]]
    (select-map-point-fx db (random-uuid) coordinates)))

(rf/reg-event-fx ::select-point-group
  (fn [{:keys [db]} [_ group]]
    (let [{:keys [coordinates] current-group :group} (get-in db [::data ::point])]
      (when (and group coordinates (not= group current-group))
        (select-map-point-fx (assoc-in db [::data ::point-group] group) (random-uuid) coordinates)))))

(rf/reg-event-fx ::point-groups.success
  (fn [{:keys [db]} [_ point-id status groups]]
    (let [db (set-groups db status groups)]
      (if (current-point? db point-id)
        (point-search-fx db point-id)
        {:db db}))))

(rf/reg-event-db ::point-groups.done
  (fn [db [_ point-id]]
    ;; Other than connection errors, a failed request was notified of already.
    (cond-> db
      (and (point-loading? db point-id)
           (not (get-in db [::data ::point :group])))
      drop-point)))

(rf/reg-event-fx ::point-failed
  (fn [{:keys [db]} [_ point-id]]
    ;; Network failures skip :finally-dispatch, and :on-error replaces the default notification.
    (when (point-loading? db point-id)
      {:db (drop-point db)
       :fx [[:dispatch [:notifications/add :toast :error
                        :error/connection-error-title
                        :error/connection-error-description]]]})))

(defn- found-point-fx [db point-id feature]
  (let [{:keys [group]} (get-in db [::data ::point])
        db              (-> db
                            (update-in [::data ::point] assoc :status :found :property feature)
                            (dashboard-map/select-point (-> (dashboard-map/selected-point db)
                                                            (p/assoc-if :geometry (:geometry feature))))
                            remember-selected-group
                            (complaint-subs/set-selected-group group))
        command         (property-complaints-command db point-id)]
    {:db db
     :fx (cond-> (into [[:dispatch [::entities/merge-entity-db
                                    (get-in db [::data ::groups-status] 200)
                                    (->> (:authority-groups group)
                                         (mapcat #(concat (:categories %) (:complaint-sources %)))
                                         du/entities->entity-db)]]]
                       (map (fn [{:group/keys [id]}]
                              [:dispatch [::ws-get/get-workspace-config id]]))
                       (:authority-groups group))
           command (conj [:comm/command command]))}))

(rf/reg-event-fx ::point-search.success
  (fn [{:keys [db]} [_ point-id _status feature]]
    (when (point-loading? db point-id)
      (found-point-fx db point-id feature))))

(rf/reg-event-db ::point-search.not-found
  (fn [db [_ point-id]]
    (cond-> db
      (point-loading? db point-id) (assoc-in [::data ::point :status] :not-found))))

(rf/reg-event-db ::point-search.done
  (fn [db [_ point-id]]
    ;; Other than connection errors, a failed request was notified of already.
    (cond-> db
      (point-loading? db point-id) drop-point)))

(defn property-complaints-success-fx [db point-id complaints]
  (when (current-point? db point-id)
    (let [first-load? (not (contains? (get-in db [::data ::point]) :complaints))]
      (p/assoc-if {:db (assoc-in db [::data ::point :complaints] complaints)}
                  ;; Same as ::complaint-subs/find-property-complaints.success
                  :fx (when (and first-load?
                                 (empty? complaints)
                                 (not (ff/enabled? :feature-flag.id/streamline-ce-admin-process)))
                        [[:dispatch [::complaint-form/start-create-complaint]]])))))

(rf/reg-event-fx ::property-complaints.success
  (fn [{:keys [db]} [_ point-id _status complaints]]
    (property-complaints-success-fx db point-id complaints)))

(defn point-refresh-fx [db point-id]
  (let [{:keys [dispatch]} (refresh-fx db)
        command            (when (and (current-point? db point-id)
                                      (= :found (get-in db [::data ::point :status])))
                             (property-complaints-command db point-id))]
    {:fx (cond-> []
           dispatch (conj [:dispatch dispatch])
           command  (conj [:comm/command command]))}))

(rf/reg-event-fx ::refresh-point
  (fn [{:keys [db]} [_ point-id]]
    (point-refresh-fx db point-id)))

(defn point-complaint-created-fx
  "The created complaint is not added to the results, as it may not match the current filters."
  [db {:property/keys [id]}]
  (let [{point-id :id :as point} (get-in db [::data ::point])]
    (point-refresh-fx db (when (= id (-> point :property :property :property/id))
                           point-id))))

(rf/reg-event-fx ::point-complaint-created
  (fn [{:keys [db]} [_ complaint]]
    (point-complaint-created-fx db complaint)))

(defn point-case-cleanup-fx [db point-id {:keys [case-workspace-id]}]
  (when case-workspace-id
    (cond-> (point-refresh-fx db point-id)
      (current-point? db point-id)
      (assoc :db (update db ::complaint-subs/data dissoc
                         ::complaint-subs/complaints-to-case
                         ::complaint-subs/complaint-in-view)))))

(rf/reg-event-fx ::point-case-cleanup
  (fn [{:keys [db]} [_ point-id cleanup-context]]
    (point-case-cleanup-fx db point-id cleanup-context)))

(defn point-action-events [point-id]
  {:refresh-event [::refresh-point point-id]
   :cleanup-event [::point-case-cleanup point-id]})

(rf/reg-sub ::action-context-available?
  :<- [::data]
  :<- [::ws-get/state]
  (fn [[data workspace-state] _]
    (and (::action-context-available? data)
         (= :done (get-in workspace-state [:config (::action-group-id data)])))))

(rf/reg-sub ::action-context-loading?
  :<- [::data]
  :<- [::ws-get/state]
  (fn [[data workspace-state] _]
    (let [workspace-status (get-in workspace-state [:config (::action-group-id data)])]
      (or (::action-context-loading? data)
          (and (::action-context-available? data)
               (contains? #{nil :fetching} workspace-status))))))

(rf/reg-sub ::complaint-id
  :<- [::data]
  :-> ::complaint-id)

(rf/reg-sub ::complaint-loaded?
  :<- [::data]
  :-> ::complaint-loaded?)

(rf/reg-sub ::point
  :<- [::data]
  :-> ::point)
